import type CreativeSDK from '@cesdk/cesdk-js';
import { RGBAColor } from '@cesdk/cesdk-js';
import { GraphicData, WebRoutes, getSizedImageSourceSet, withDomain } from '@theprintbar/shared';
import { getBoundingBox, getOverlappingBoundingBox, getUserAddedBlocks, isGraphicBlock, isImageBlock, isUserAddedBlock } from './block';
import { designAssetMeta, designLocalAssetMeta } from './customisation/assets/images/designs';
import { getPageScaleFactor } from './page';

interface AddGraphicOptions {
	url: string;
	editor: CreativeSDK;
	page: number;
	height: number;
	width: number;
	top?: number;
	meta?: Record<string, string>;
}

export function addGraphic({ url, editor, page, height, width, top, meta }: AddGraphicOptions) {
	const scaleFactor = getPageScaleFactor(editor.engine, page);
	const { block } = editor.engine;
	const graphicBlock = block.create('//ly.img.ubq/graphic');
	const rectShape = block.createShape('rect');
	const fill = block.createFill('//ly.img.ubq/fill/image');
	block.setSourceSet(fill, 'fill/image/sourceSet', [
		{
			width: width * scaleFactor,
			height: height * scaleFactor,
			uri: url,
		},
	]);
	block.setShape(graphicBlock, rectShape);
	block.setFill(graphicBlock, fill);
	block.appendChild(page, graphicBlock);
	block.setHeight(graphicBlock, height * scaleFactor);
	block.setWidth(graphicBlock, width * scaleFactor);
	//See addText: manual centre — alignHorizontally isn't reliable for a single block in 1.77.
	const pageWidth = block.getWidth(page);
	block.setPositionX(graphicBlock, (pageWidth - width * scaleFactor) / 2);
	block.setPositionY(graphicBlock, scaleFactor * (top ?? 0));

	Object.entries(meta ?? {}).forEach(([key, value]) => {
		block.setMetadata(graphicBlock, key, value);
	});

	return graphicBlock;
}

export function getGraphicData(editor: CreativeSDK, page: number, graphicBlock: number): GraphicData | null {
	const { block } = editor.engine;
	//Get page block is on;
	const scaleFactor = getPageScaleFactor(editor.engine, page);
	const { hasCutoff } = getOverlappingBoundingBox(editor, [graphicBlock], page) || {};

	if (!isUserAddedBlock(editor, graphicBlock) || !isImageBlock(block.getType(graphicBlock), editor, graphicBlock)) return null;
	const fill = block.getFill(graphicBlock);
	const sourceSet = block.getSourceSet(fill, 'fill/image/sourceSet');
	const originalSet = sourceSet?.find(set => !set.uri.includes('size=')) || sourceSet[0];
	if (!originalSet) return null;
	let localId = block.hasMetadata(graphicBlock, designLocalAssetMeta) ? block.getMetadata(graphicBlock, designLocalAssetMeta) : undefined;
	const designId = block.hasMetadata(graphicBlock, designAssetMeta) ? block.getMetadata(graphicBlock, designAssetMeta) : undefined;
	const sourceSetId = originalSet.uri.split('/').pop() || 'unknown';
	if (!!localId && localId !== sourceSetId) {
		block.setMetadata(graphicBlock, designLocalAssetMeta, sourceSetId);
		block.removeMetadata(graphicBlock, designAssetMeta);
		localId = sourceSetId;
	}

	const designIdUri = designId?.split('|')[1];
	const cleanedUri = originalSet.uri.split('?')[0].split('/').pop();
	if (!!designIdUri && designIdUri !== cleanedUri) {
		//Something is wrong, revert it to a local image
		console.warn('Design ID URI does not match sourceSet URI, reverting to local image');
		block.removeMetadata(graphicBlock, designAssetMeta);
		block.setMetadata(graphicBlock, designLocalAssetMeta, sourceSetId);
	}

	const { width, height } = getBoundingBox(editor.engine, graphicBlock);

	const mmWidth = width / scaleFactor;
	const mmHeight = height / scaleFactor;
	const isSVG = !!cleanedUri?.endsWith('.svg');

	//Get the lowest DPI from either the width dpi or the height dpi
	const widthDpi = isSVG ? 300 : Math.round((originalSet.width / mmWidth) * 25.4);
	const heightDpi = isSVG ? 300 : Math.round((originalSet.height / mmHeight) * 25.4);

	//Check if any rotation or crop or skew has been applied to the graphic
	const rotated = block.getRotation(graphicBlock) !== 0;
	const fliipped = block.getFlipHorizontal(graphicBlock) || block.getFlipVertical(graphicBlock);
	// Compare source vs block aspect ratio with a relative tolerance rather than
	// rounding to 2 decimals: for extreme ratios (very tall/wide images) sub-1%
	// discrepancies from CE.SDK's own rounding would spuriously trip `skewed` and
	// disqualify the graphic from the source-crop pipeline. 2% is loose enough to
	// absorb that noise while still catching genuine non-uniform scaling.
	const sourceSetRatio = originalSet.width / originalSet.height;
	const boundingBoxRatio = mmWidth / mmHeight;
	const skewed = Math.abs(sourceSetRatio - boundingBoxRatio) / sourceSetRatio > 0.02;
	const stroke = !!block.getStrokeWidth(graphicBlock) && (block.getStrokeColor(graphicBlock) as RGBAColor).a < 1;
	const estimatedSize = originalSet.width * originalSet.height * 4;

	const graphicData: GraphicData = {
		block: graphicBlock,
		hasCutoff,
		localImage: !!block.hasMetadata(graphicBlock, designLocalAssetMeta),
		designId: block.hasMetadata(graphicBlock, designAssetMeta) ? block.getMetadata(graphicBlock, designAssetMeta) : undefined,
		dpi: Math.min(widthDpi, heightDpi),
		manipulated: rotated || fliipped || skewed || stroke,
		fileSize: block.hasMetadata(graphicBlock, 'fileSize')
			? parseInt(block.getMetadata(graphicBlock, 'fileSize') || `${estimatedSize}`, 10)
			: undefined,
		fileWidth: originalSet.width,
		fileHeight: originalSet.height,
	};

	return graphicData;
}

export async function ensureGraphicBlocksHaveCorrectURLs(editor: CreativeSDK, webDomain: string) {
	const { block } = editor.engine;
	const graphicBlocks = getUserAddedBlocks(editor).filter(b => isImageBlock(block.getType(b), editor, b));
	for (const graphicBlock of graphicBlocks) {
		const fill = block.getFill(graphicBlock);
		const sourceSet = block.getSourceSet(fill, 'fill/image/sourceSet')?.[0];
		if (!sourceSet) continue;

		//Get the filename from the URL
		const upload = sourceSet.uri.split('/').pop();
		if (!upload) continue;
		//If the URL doesn't include WebRoutes.API.images.viewUpload we need to update it
		if (!sourceSet.uri.includes(WebRoutes.API.images.viewUpload(upload))) {
			console.log('Updating graphic block URL for block', graphicBlock);
			block.setSourceSet(
				fill,
				'fill/image/sourceSet',
				getSizedImageSourceSet({
					url: withDomain(WebRoutes.API.images.viewUpload(upload), webDomain),
					originalHeight: sourceSet.height,
					originalWidth: sourceSet.width,
				}),
			);
		}
	}
}
